'use client'
import { useEffect, useRef } from 'react'

// WebOTP API type definitions
declare global {
  interface CredentialRequestOptions {
    otp?: {
      transport: string[]
    }
  }

  interface Credential {
    code?: string
  }
}

interface UseWebOtpOptions {
  onOtpReceived?: (otp: string) => void
  enabled?: boolean
}

export const useWebOtp = ({
  onOtpReceived,
  enabled = true,
}: UseWebOtpOptions) => {
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!enabled || !onOtpReceived) return

    // Check if WebOTP API is supported
    if (!('OTPCredential' in window)) {
      return
    }

    const handleOtp = async () => {
      try {
        // Create abort controller for cleanup
        abortControllerRef.current = new AbortController()

        // Request OTP from SMS
        const otp = (await navigator.credentials.get({
          otp: { transport: ['sms'] },
          signal: abortControllerRef.current.signal,
        })) as Credential

        if (otp?.code) {
          onOtpReceived(otp.code)
        }
      } catch (error) {
        // User cancelled or error occurred
        console.log('WebOTP cancelled or error:', error)
      }
    }

    // Start listening for OTP
    handleOtp()

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [enabled, onOtpReceived])

  // Manual cleanup function
  const abort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  return {
    abort,
    isSupported: 'OTPCredential' in window,
  }
}
